import maplibregl from "maplibre-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Link } from "react-router-dom";

import {
  checkpointDraftFromRow,
  fetchCheckpointSetupList,
  fetchPlacementCheckpoints,
  splitPlacementQueues,
  updateCheckpoint,
  type CheckpointSetupRow,
  type PlacementCheckpointRow,
} from "../data/checkpoint-setup";
import {
  classifyFieldPin,
  countFieldPinLayers,
  fieldPinLayerLabelWithCount,
  fieldPinLayerStyle,
  FIELD_PIN_LAYERS,
  toFieldPinFeatureCollection,
  type FieldPin,
} from "../data/field-pin-layers";
import type { ManagedSite } from "../data/clients-sites";
import type { SiteRow } from "../data/routes";
import { formatGeocodeQuery, geocodeAddress } from "../lib/geocode";
import {
  checkpointColor,
  toCheckpointFeatureCollection,
  toGeofenceFeatureCollection,
  type CheckpointPoint,
} from "../data/map-points";
import {
  clearSiteBoundary,
  fetchSiteBoundaries,
  normalizeDrawRing,
  saveSiteBoundary,
  toDraftBoundaryFillFeatureCollection,
  toDraftBoundaryLineFeatureCollection,
  toDraftBoundaryVertexFeatureCollection,
  toSiteBoundaryFeatureCollection,
  type Ring,
  type SiteBoundary,
} from "../data/site-boundary";
import {
  fetchSiteAssets,
  findContainingSiteAsset,
  type SiteAsset,
} from "../data/site-asset";
import {
  CHECKPOINT_PIN_PAINT,
  DRAFT_BOUNDARY_VERTEX_PAINT,
  FIELD_PIN_DRAFT_PAINT,
  FIELD_PIN_PAINT,
  ensureGeoJsonSource,
  ensureMapLayer,
  findOverlayInsertBeforeId,
  repositionMapOverlayLayers,
  runMapOverlayRefresh,
  syncGeoJsonSource,
  watchCadOverlayStack,
  whenMapStyleReady,
} from "../lib/map-cad-layers";
import { MAP_THEME } from "../lib/map-theme";
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, mapStyleUrl } from "../lib/map-style-url";
import { spreadStackedMarkerPositions } from "../lib/map-marker-spread";
import { renderPropertyMapSvgOverlay } from "../lib/property-map-svg-overlay";
import { resolveSiteMapViewport } from "../lib/map-site-viewport";
import { useReadModel } from "../data/useReadModel";

const BOUNDARY_SOURCE = "property-boundary";
const BOUNDARY_FILL_LAYER = "property-boundary-fill";
const BOUNDARY_LINE_LAYER = "property-boundary-line";
const DRAFT_LINE_SOURCE = "property-boundary-draft-line";
const DRAFT_FILL_SOURCE = "property-boundary-draft-fill";
const DRAFT_VERTEX_SOURCE = "property-boundary-draft-vertices";
const DRAFT_FILL_LAYER = "property-boundary-draft-fill";
const DRAFT_LINE_LAYER = "property-boundary-draft-line";
const DRAFT_VERTEX_LAYER = "property-boundary-draft-vertices";
const GEOFENCE_SOURCE = "property-geofences";
const GEOFENCE_FILL_LAYER = "property-geofence-fill";
const GEOFENCE_LINE_LAYER = "property-geofence-line";
const CHECKPOINT_SOURCE = "property-checkpoints";
const CHECKPOINT_LAYER = "property-checkpoint-pins";
// PRD-001Y Wave C. Separate source from CHECKPOINT_SOURCE on purpose: that one feeds the
// editable routed pins, which are also drawn as draggable HTML markers and are the only pins
// this surface can move. Field pins are read-only here, so keeping them in their own source
// means a drag or a pending-coordinate edit can never accidentally address one.
const FIELD_PIN_SOURCE = "property-field-pins";
const FIELD_UNROUTED_LAYER = "property-field-pins-unrouted";
const FIELD_DRAFT_LAYER = "property-field-pins-draft";
const SITE_CENTER_SOURCE = "property-site-center";
const SITE_CENTER_LAYER = "property-site-center-pin";

const OVERLAY_LAYERS = [
  BOUNDARY_FILL_LAYER,
  BOUNDARY_LINE_LAYER,
  DRAFT_FILL_LAYER,
  DRAFT_LINE_LAYER,
  GEOFENCE_FILL_LAYER,
  GEOFENCE_LINE_LAYER,
  SITE_CENTER_LAYER,
  // Read-only field pins sit below the editable routed pins so a draft can never sit on top of
  // the marker an operator is trying to drag.
  FIELD_DRAFT_LAYER,
  FIELD_UNROUTED_LAYER,
  CHECKPOINT_LAYER,
  DRAFT_VERTEX_LAYER,
] as const;

type PlacementMode = "view" | "draw-boundary" | "place-checkpoint";

function setupRowToMapPoint(row: CheckpointSetupRow): CheckpointPoint | null {
  if (row.expectedLat === null || row.expectedLng === null) {
    return null;
  }
  return {
    checkpointId: row.checkpointId,
    name: row.checkpointName,
    lat: row.expectedLat,
    lng: row.expectedLng,
    verificationStatus: row.verificationStatus,
    preferredMethod: row.preferredMethod,
    geofenceRadiusMeters: row.geofenceRadiusMeters,
    routeName: row.routeName,
    siteName: row.siteName,
    isRequired: row.isRequired,
    // Every row from the setup view is route-attached by construction (the view is
    // route-rooted), so these pins are always the routed bucket. Unrouted and provisional pins
    // reach this surface through the placement reader instead, below.
    routeId: row.routeId,
    placementState: row.placementState,
    placementSource: row.placementSource,
  };
}

/** PRD-001Y Wave C: the pins the route-rooted setup view structurally cannot show. */
function placementRowToFieldPin(row: PlacementCheckpointRow): FieldPin | null {
  if (row.expectedLat === null || row.expectedLng === null) {
    return null;
  }
  return {
    checkpointId: row.checkpointId,
    name: row.checkpointName,
    lat: row.expectedLat,
    lng: row.expectedLng,
    layer: classifyFieldPin({ placementState: row.placementState, isRouted: false }),
    siteName: null,
    routeName: null,
    // The placement reader filters on the `active` BOOLEAN, which is not the same column as
    // `verification_status`. An active checkpoint can still be in maintenance, so carry the
    // status through: without it every unrouted and draft pin would paint healthy, which is the
    // same fabricate-state defect SEC-C-01 fixed on CAD.
    verificationStatus: row.verificationStatus,
  };
}

function siteCenterFor(site: ManagedSite | undefined): { lat: number; lng: number } | null {
  if (site?.centerLat !== null && site?.centerLat !== undefined && site.centerLng !== null && site.centerLng !== undefined) {
    return { lat: site.centerLat, lng: site.centerLng };
  }
  return null;
}

function toSiteCenterFeatureCollection(center: { lat: number; lng: number } | null): {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: Record<string, string>;
  }>;
} {
  if (center === null) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: [center.lng, center.lat] },
        properties: { label: "Site center" },
      },
    ],
  };
}

function createMapPinElement(className: string, color: string): HTMLDivElement {
  const element = document.createElement("div");
  element.className = className;
  element.style.backgroundColor = color;
  return element;
}

function syncCheckpointMarkers(
  map: maplibregl.Map,
  markers: Map<string, maplibregl.Marker>,
  points: readonly CheckpointPoint[],
): void {
  const spreadPoints = spreadStackedMarkerPositions(points);
  const nextIds = new Set(spreadPoints.map((point) => point.checkpointId));
  for (const [checkpointId, marker] of markers) {
    if (!nextIds.has(checkpointId)) {
      marker.remove();
      markers.delete(checkpointId);
    }
  }
  for (const point of spreadPoints) {
    const position: [number, number] = [point.displayLng, point.displayLat];
    const existing = markers.get(point.checkpointId);
    if (existing !== undefined) {
      existing.setLngLat(position);
      continue;
    }
    const marker = new maplibregl.Marker({
      element: createMapPinElement("property-map__pin", checkpointColor(point.verificationStatus)),
      anchor: "center",
    })
      .setLngLat(position)
      .addTo(map);
    markers.set(point.checkpointId, marker);
  }
}

function syncDraftVertexMarkers(
  map: maplibregl.Map,
  markers: maplibregl.Marker[],
  ring: Ring,
  active: boolean,
): void {
  for (const marker of markers) {
    marker.remove();
  }
  markers.length = 0;
  if (!active) {
    return;
  }
  for (const position of ring) {
    const marker = new maplibregl.Marker({
      element: createMapPinElement("property-map__draft-vertex", "#5b8fff"),
      anchor: "center",
    })
      .setLngLat(position)
      .addTo(map);
    markers.push(marker);
  }
}

function clearMapMarkers(
  checkpointMarkers: Map<string, maplibregl.Marker>,
  draftMarkers: maplibregl.Marker[],
): void {
  for (const marker of checkpointMarkers.values()) {
    marker.remove();
  }
  checkpointMarkers.clear();
  for (const marker of draftMarkers) {
    marker.remove();
  }
  draftMarkers.length = 0;
}

function applyViewport(
  map: maplibregl.Map,
  boundaryRing: Ring | null,
  checkpoints: readonly { lat: number; lng: number }[],
  siteCenter: { lat: number; lng: number } | null,
): void {
  const target = resolveSiteMapViewport(boundaryRing, checkpoints, 16, siteCenter);
  if (target.mode === "none") {
    return;
  }
  if (target.mode === "point") {
    map.flyTo({ center: target.center, zoom: target.zoom, duration: 400, essential: true });
    return;
  }
  const bounds = new maplibregl.LngLatBounds(target.southwest, target.northeast);
  map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 400 });
}

async function applyViewportWithSiteFallback(
  map: maplibregl.Map,
  boundaryRing: Ring | null,
  checkpoints: readonly { lat: number; lng: number }[],
  activeSite: ManagedSite | undefined,
  mapTilerKey: string | undefined,
  isCurrent: () => boolean,
): Promise<void> {
  const siteCenter = siteCenterFor(activeSite);
  const target = resolveSiteMapViewport(boundaryRing, checkpoints, 16, siteCenter);
  if (target.mode !== "none") {
    applyViewport(map, boundaryRing, checkpoints, siteCenter);
    return;
  }
  if (activeSite === undefined || !isCurrent()) {
    return;
  }
  const query = formatGeocodeQuery(activeSite);
  if (query.length < 5) {
    return;
  }
  const outcome = await geocodeAddress(query, mapTilerKey);
  if (!isCurrent() || !outcome.ok) {
    return;
  }
  map.flyTo({
    center: [outcome.result.lng, outcome.result.lat],
    zoom: 16,
    duration: 400,
    essential: true,
  });
}

export function PropertyMapEditor({
  client,
  accountId,
  canManage,
  mapTilerKey,
  pickerSites,
  managedSites,
  initialSiteId,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  mapTilerKey?: string | undefined;
  pickerSites: readonly SiteRow[];
  managedSites: readonly ManagedSite[];
  initialSiteId?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgOverlayRef = useRef<SVGSVGElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const checkpointMarkersRef = useRef(new Map<string, maplibregl.Marker>());
  const draftMarkersRef = useRef<maplibregl.Marker[]>([]);
  const viewportRequestRef = useRef(0);
  const modeRef = useRef<PlacementMode>("view");
  const selectedCheckpointRef = useRef<string | null>(null);
  const draftRingRef = useRef<Ring>([]);
  const boundaryForSiteRef = useRef<SiteBoundary | null>(null);
  const displayedCheckpointsRef = useRef<CheckpointPoint[]>([]);
  const fieldPinsRef = useRef<FieldPin[]>([]);
  const siteAssetsRef = useRef<SiteAsset[]>([]);
  const activeSiteRef = useRef<ManagedSite | undefined>(undefined);
  const onMapClickRef = useRef<(event: maplibregl.MapMouseEvent) => void>(() => {});
  const saveVerifyRef = useRef<{
    checkpointId: string;
    lat: number;
    lng: number;
    name: string;
  } | null>(null);

  const [selectedSiteId, setSelectedSiteId] = useState(
    initialSiteId ?? pickerSites[0]?.siteId ?? "",
  );
  const [mode, setMode] = useState<PlacementMode>("view");
  const [draftRing, setDraftRing] = useState<Ring>([]);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string | null>(null);
  const [pendingCoords, setPendingCoords] = useState<Record<string, { lat: number; lng: number }>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [recenterToken, setRecenterToken] = useState(0);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);

  const boundaryModel = useReadModel(
    () => fetchSiteBoundaries(client, accountId),
    [client, accountId],
  );
  const checkpointModel = useReadModel(
    () => fetchCheckpointSetupList(client, accountId),
    [client, accountId],
  );
  const assetModel = useReadModel(
    () => fetchSiteAssets(client, accountId),
    [client, accountId],
  );
  // PRD-001Y Wave C. fetchCheckpointSetupList is route-rooted, so a pin planted on a field walk
  // was invisible on this map until someone attached it to a route. This is the same reader
  // CheckpointEditor already uses on the Setup surface (RLS-scoped, active pins only, never the
  // placer id), so the property map gains no audience the Setup lists did not already have.
  const placementModel = useReadModel(
    () => fetchPlacementCheckpoints(client, accountId),
    [client, accountId],
  );

  const activeSiteId = selectedSiteId.length > 0 ? selectedSiteId : (pickerSites[0]?.siteId ?? "");
  const activeSite =
    managedSites.find((site) => site.siteId === activeSiteId) ??
    managedSites.find((site) => site.siteId === (pickerSites[0]?.siteId ?? "")) ??
    undefined;
  const boundaryForSite =
    (boundaryModel.data ?? []).find((boundary) => boundary.siteId === activeSiteId) ?? null;
  const siteCheckpoints = useMemo(
    () =>
      (checkpointModel.data ?? []).filter(
        (row) => row.siteId === activeSiteId && row.routeActive && row.checkpointActive,
      ),
    [checkpointModel.data, activeSiteId],
  );
  const uniqueSiteCheckpoints = useMemo(() => {
    const byId = new Map<string, CheckpointSetupRow>();
    for (const row of siteCheckpoints) {
      const existing = byId.get(row.checkpointId);
      if (existing === undefined) {
        byId.set(row.checkpointId, row);
        continue;
      }
      if (
        (existing.expectedLat === null || existing.expectedLng === null) &&
        row.expectedLat !== null &&
        row.expectedLng !== null
      ) {
        byId.set(row.checkpointId, row);
      }
    }
    return [...byId.values()];
  }, [siteCheckpoints]);
  const siteAssetsForSite = useMemo(
    () => (assetModel.data ?? []).filter((asset) => asset.siteId === activeSiteId),
    [assetModel.data, activeSiteId],
  );

  const assetLabelForCheckpoint = useCallback(
    (row: CheckpointSetupRow, pending?: { lat: number; lng: number }) => {
      const lat = pending?.lat ?? row.expectedLat;
      const lng = pending?.lng ?? row.expectedLng;
      if (lat === null || lng === null) {
        return null;
      }
      return findContainingSiteAsset(siteAssetsForSite, lat, lng)?.name ?? null;
    },
    [siteAssetsForSite],
  );

  const displayedCheckpoints = useMemo(() => {
    return uniqueSiteCheckpoints
      .map((row) => {
      const pending = pendingCoords[row.checkpointId];
      if (pending !== undefined) {
        return setupRowToMapPoint({
          ...row,
          expectedLat: pending.lat,
          expectedLng: pending.lng,
        });
      }
      return setupRowToMapPoint(row);
    })
      .filter((point): point is CheckpointPoint => point !== null);
  }, [uniqueSiteCheckpoints, pendingCoords]);

  /**
   * Wave C unrouted and draft pins for THIS site. Routed ids are subtracted against the whole
   * account's setup rows, not just this site's, so a pin routed on another site can never leak
   * into the unrouted bucket here. Same partition helper the Setup queues use, so the map and
   * the list cannot disagree about what counts as a draft.
   *
   * The bucket is defined by SUBTRACTION, so it only means anything once the routed read has
   * actually succeeded. fetchCheckpointSetupList throws on failure, so while that read is
   * loading or errored its data is null and the subtraction set is empty. An empty set does not
   * mean "nothing is routed", it means "not known yet", and publishing on it would repaint
   * every routed stop on the property as a sky-blue unrouted pin with a legend count to match.
   * That is the same fabricate-state failure Wave C exists to end, pointed the other way, so
   * hold the buckets back instead. An empty field-pin layer is the honest degrade and is
   * exactly what this surface showed before Wave C.
   */
  const sitePlacementPins = useMemo<FieldPin[]>(() => {
    if (checkpointModel.status !== "ready" || checkpointModel.data === null) {
      return [];
    }
    const routedCheckpointIds = new Set(
      checkpointModel.data.map((row) => row.checkpointId),
    );
    const { walkDrafts, unroutedConfirmed } = splitPlacementQueues(
      placementModel.data ?? [],
      routedCheckpointIds,
    );
    return [...walkDrafts, ...unroutedConfirmed]
      .filter((row) => row.siteId === activeSiteId)
      .map(placementRowToFieldPin)
      .filter((pin): pin is FieldPin => pin !== null);
  }, [placementModel.data, checkpointModel.data, checkpointModel.status, activeSiteId]);

  /** Routed pins plus the Wave C buckets, in one collection for the shared taxonomy layers. */
  const fieldPins = useMemo(
    () => [
      ...displayedCheckpoints.map((point) => ({
        checkpointId: point.checkpointId,
        name: point.name,
        lat: point.lat,
        lng: point.lng,
        layer: classifyFieldPin({ placementState: point.placementState, isRouted: true }),
        siteName: point.siteName,
        routeName: point.routeName,
      })),
      ...sitePlacementPins,
    ],
    [displayedCheckpoints, sitePlacementPins],
  );
  const fieldPinCounts = useMemo(() => countFieldPinLayers(fieldPins), [fieldPins]);

  const geofencePreviewCount = useMemo(
    () =>
      displayedCheckpoints.filter(
        (point) => point.geofenceRadiusMeters !== null && point.geofenceRadiusMeters > 0,
      ).length,
    [displayedCheckpoints],
  );

  const stackedStopCount = useMemo(() => {
    const keys = new Set(
      displayedCheckpoints.map((point) => `${point.lat.toFixed(5)}:${point.lng.toFixed(5)}`),
    );
    return Math.max(0, displayedCheckpoints.length - keys.size);
  }, [displayedCheckpoints]);

  const updateSvgOverlay = useCallback(() => {
    const map = mapRef.current;
    const svg = svgOverlayRef.current;
    if (map === null || svg === null || !map.isStyleLoaded()) {
      return;
    }
    const currentMode = modeRef.current;
    renderPropertyMapSvgOverlay(map, svg, {
      boundaryRing:
        boundaryForSiteRef.current !== null && currentMode !== "draw-boundary"
          ? boundaryForSiteRef.current.ring
          : null,
      assetZones: siteAssetsRef.current,
      draftRing: currentMode === "draw-boundary" ? draftRingRef.current : [],
      draftActive: currentMode === "draw-boundary",
      checkpoints: displayedCheckpointsRef.current,
    });
  }, []);

  const resetFeedback = useCallback(() => {
    setFailure(null);
    setReceipt(null);
  }, []);

  const frameMapViewport = useCallback(async () => {
    const map = mapRef.current;
    if (map === null) {
      return;
    }
    const requestId = viewportRequestRef.current + 1;
    viewportRequestRef.current = requestId;
    await applyViewportWithSiteFallback(
      map,
      boundaryForSite?.ring ?? null,
      displayedCheckpoints.map((point) => ({ lat: point.lat, lng: point.lng })),
      activeSite,
      mapTilerKey,
      () => viewportRequestRef.current === requestId,
    );
  }, [activeSite, boundaryForSite, displayedCheckpoints, mapTilerKey]);

  const enterDrawBoundary = useCallback(() => {
    if (!canManage) {
      return;
    }
    resetFeedback();
    setMode("draw-boundary");
    setSelectedCheckpointId(null);
    setDraftRing([]);
  }, [canManage, resetFeedback]);

  const cancelDrawBoundary = useCallback(() => {
    setMode("view");
    setDraftRing([]);
  }, []);

  const saveBoundary = useCallback(async () => {
    if (!canManage || busy || draftRing.length < 3) {
      return;
    }
    resetFeedback();
    setBusy(true);
    const outcome = await saveSiteBoundary(client, accountId, activeSiteId, draftRing, {
      source: "drawn",
    });
    setBusy(false);
    if (outcome.ok) {
      const savedRing = normalizeDrawRing(draftRing);
      setReceipt("Site boundary saved.");
      setMode("view");
      setDraftRing([]);
      boundaryModel.reload();
      const map = mapRef.current;
      if (map !== null) {
        whenMapStyleReady(map, () => {
          applyViewport(
            map,
            savedRing,
            displayedCheckpoints.map((point) => ({ lat: point.lat, lng: point.lng })),
            siteCenterFor(activeSite),
          );
        });
      }
    } else {
      setFailure(outcome.reason);
    }
  }, [
    canManage,
    busy,
    draftRing,
    resetFeedback,
    client,
    accountId,
    activeSiteId,
    activeSite,
    boundaryModel,
    displayedCheckpoints,
  ]);

  const clearBoundary = useCallback(async () => {
    if (!canManage || busy || boundaryForSite === null) {
      return;
    }
    resetFeedback();
    setBusy(true);
    const outcome = await clearSiteBoundary(client, accountId, activeSiteId);
    setBusy(false);
    if (outcome.ok) {
      setReceipt("Site boundary cleared.");
      setDraftRing([]);
      boundaryModel.reload();
    } else {
      setFailure(outcome.reason);
    }
  }, [canManage, busy, boundaryForSite, resetFeedback, client, accountId, activeSiteId, boundaryModel]);

  const saveCheckpointPlacement = useCallback(async () => {
    if (!canManage || busy || selectedCheckpointId === null) {
      return;
    }
    const pending = pendingCoords[selectedCheckpointId];
    const row = uniqueSiteCheckpoints.find((entry) => entry.checkpointId === selectedCheckpointId);
    if (pending === undefined || row === undefined) {
      setFailure("Pick a stop and click the map to place it first.");
      return;
    }
    resetFeedback();
    setBusy(true);
    const draft = checkpointDraftFromRow(row, {
      expectedLat: pending.lat,
      expectedLng: pending.lng,
    });
    const outcome = await updateCheckpoint(client, accountId, row.checkpointId, draft);
    setBusy(false);
    if (outcome.ok) {
      saveVerifyRef.current = {
        checkpointId: row.checkpointId,
        lat: pending.lat,
        lng: pending.lng,
        name: row.checkpointName,
      };
      setReceipt(`${row.checkpointName} saved on map.`);
      setSelectedCheckpointId(null);
      setMode("view");
      checkpointModel.reload();
    } else {
      setFailure(outcome.reason);
    }
  }, [
    canManage,
    busy,
    selectedCheckpointId,
    pendingCoords,
    uniqueSiteCheckpoints,
    resetFeedback,
    client,
    accountId,
    checkpointModel,
  ]);

  useEffect(() => {
    const watch = saveVerifyRef.current;
    if (watch === null || checkpointModel.status !== "ready") {
      return;
    }
    saveVerifyRef.current = null;
    const row = uniqueSiteCheckpoints.find((entry) => entry.checkpointId === watch.checkpointId);
    const persisted =
      row !== undefined &&
      row.expectedLat !== null &&
      row.expectedLng !== null &&
      Math.abs(row.expectedLat - watch.lat) < 1e-6 &&
      Math.abs(row.expectedLng - watch.lng) < 1e-6;
    if (persisted) {
      setPendingCoords((current) => {
        if (current[watch.checkpointId] === undefined) {
          return current;
        }
        const next = { ...current };
        delete next[watch.checkpointId];
        return next;
      });
      return;
    }
    setFailure(`${watch.name} did not stick after save. Check your connection and try again.`);
  }, [checkpointModel.lastRefreshedAt, checkpointModel.status, uniqueSiteCheckpoints]);

  modeRef.current = mode;
  selectedCheckpointRef.current = selectedCheckpointId;
  draftRingRef.current = draftRing;
  boundaryForSiteRef.current = boundaryForSite;
  displayedCheckpointsRef.current = displayedCheckpoints;
  fieldPinsRef.current = fieldPins;
  siteAssetsRef.current = siteAssetsForSite;
  activeSiteRef.current = activeSite;

  onMapClickRef.current = (event) => {
    const lngLat: [number, number] = [event.lngLat.lng, event.lngLat.lat];
    if (modeRef.current === "draw-boundary" && canManage) {
      setDraftRing((current) => [...current, lngLat]);
      return;
    }
    if (modeRef.current === "place-checkpoint" && canManage && selectedCheckpointRef.current !== null) {
      const checkpointId = selectedCheckpointRef.current;
      setPendingCoords((current) => ({
        ...current,
        [checkpointId]: { lat: lngLat[1], lng: lngLat[0] },
      }));
    }
  };

  const canMountMap = pickerSites.length > 0;

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || !canMountMap) {
      return;
    }

    setMapReadyVersion(0);
    const styleUrl = mapStyleUrl("hybrid", mapTilerKey);
    const map = new maplibregl.Map({
      container,
      style:
        styleUrl === "blank"
          ? {
              version: 8,
              sources: {},
              layers: [
                {
                  id: "background",
                  type: "background",
                  paint: { "background-color": MAP_THEME.blankBackground },
                },
              ],
            }
          : styleUrl,
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    const clickHandler = (event: maplibregl.MapMouseEvent) => {
      onMapClickRef.current(event);
    };
    map.on("click", clickHandler);
    const redrawSvgOverlay = () => {
      updateSvgOverlay();
    };
    map.on("move", redrawSvgOverlay);
    map.on("zoom", redrawSvgOverlay);
    map.on("rotate", redrawSvgOverlay);
    map.on("resize", redrawSvgOverlay);
    map.once("load", () => {
      map.resize();
      setMapReadyVersion((value) => value + 1);
      setRecenterToken((value) => value + 1);
    });
    map.on("idle", () => {
      map.resize();
    });

    const observer = new ResizeObserver(() => {
      map.resize();
    });
    observer.observe(container);
    requestAnimationFrame(() => {
      map.resize();
    });

    return () => {
      observer.disconnect();
      map.off("click", clickHandler);
      map.off("move", redrawSvgOverlay);
      map.off("zoom", redrawSvgOverlay);
      map.off("rotate", redrawSvgOverlay);
      map.off("resize", redrawSvgOverlay);
      clearMapMarkers(checkpointMarkersRef.current, draftMarkersRef.current);
      map.remove();
      mapRef.current = null;
    };
  }, [mapTilerKey, canMountMap, accountId, updateSvgOverlay]);

  const syncMapData = useCallback(() => {
    const map = mapRef.current;
    if (map === null) {
      return;
    }

    const refresh = () => {
      if (mapRef.current !== map || !map.isStyleLoaded()) {
        return;
      }

      const overlayBeforeId = findOverlayInsertBeforeId(map);

      ensureGeoJsonSource(map, BOUNDARY_SOURCE, toSiteBoundaryFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: BOUNDARY_FILL_LAYER,
          type: "fill",
          source: BOUNDARY_SOURCE,
          paint: {
            "fill-color": "rgba(91, 143, 255, 0.45)",
            "fill-opacity": 1,
          },
        },
        overlayBeforeId,
      );
      ensureMapLayer(
        map,
        {
          id: BOUNDARY_LINE_LAYER,
          type: "line",
          source: BOUNDARY_SOURCE,
          paint: {
            "line-color": "#5b8fff",
            "line-width": 4,
            "line-opacity": 1,
          },
        },
        overlayBeforeId,
      );

      ensureGeoJsonSource(map, DRAFT_FILL_SOURCE, toDraftBoundaryFillFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: DRAFT_FILL_LAYER,
          type: "fill",
          source: DRAFT_FILL_SOURCE,
          paint: {
            "fill-color": "rgba(91, 143, 255, 0.32)",
            "fill-opacity": 1,
          },
        },
        overlayBeforeId,
      );
      ensureGeoJsonSource(map, DRAFT_LINE_SOURCE, toDraftBoundaryLineFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: DRAFT_LINE_LAYER,
          type: "line",
          source: DRAFT_LINE_SOURCE,
          paint: {
            "line-color": "#5b8fff",
            "line-width": 4,
            "line-dasharray": [2, 1],
            "line-opacity": 1,
          },
        },
        overlayBeforeId,
      );
      ensureGeoJsonSource(map, DRAFT_VERTEX_SOURCE, toDraftBoundaryVertexFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: DRAFT_VERTEX_LAYER,
          type: "circle",
          source: DRAFT_VERTEX_SOURCE,
          paint: DRAFT_BOUNDARY_VERTEX_PAINT,
        },
        overlayBeforeId,
      );

      ensureGeoJsonSource(map, GEOFENCE_SOURCE, toGeofenceFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: GEOFENCE_FILL_LAYER,
          type: "fill",
          source: GEOFENCE_SOURCE,
          paint: {
            "fill-color": MAP_THEME.geofenceFill,
            "fill-opacity": 1,
          },
        },
        overlayBeforeId,
      );
      ensureMapLayer(
        map,
        {
          id: GEOFENCE_LINE_LAYER,
          type: "line",
          source: GEOFENCE_SOURCE,
          paint: {
            "line-color": MAP_THEME.geofenceStroke,
            "line-width": 2,
            "line-opacity": 0.95,
          },
        },
        overlayBeforeId,
      );

      ensureGeoJsonSource(map, SITE_CENTER_SOURCE, toSiteCenterFeatureCollection(null));
      ensureMapLayer(
        map,
        {
          id: SITE_CENTER_LAYER,
          type: "circle",
          source: SITE_CENTER_SOURCE,
          paint: {
            "circle-radius": 10,
            "circle-color": MAP_THEME.siteBoundaryStroke,
            "circle-stroke-width": 3,
            "circle-stroke-color": "#ffffff",
            "circle-opacity": 0.95,
            "circle-pitch-alignment": "viewport",
          },
        },
        overlayBeforeId,
      );

      ensureGeoJsonSource(map, FIELD_PIN_SOURCE, toFieldPinFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: FIELD_DRAFT_LAYER,
          type: "circle",
          source: FIELD_PIN_SOURCE,
          filter: ["==", ["get", "pinLayer"], "draft"],
          paint: FIELD_PIN_DRAFT_PAINT,
        },
        overlayBeforeId,
      );
      ensureMapLayer(
        map,
        {
          id: FIELD_UNROUTED_LAYER,
          type: "circle",
          source: FIELD_PIN_SOURCE,
          filter: ["==", ["get", "pinLayer"], "unrouted_confirmed"],
          paint: FIELD_PIN_PAINT,
        },
        overlayBeforeId,
      );

      ensureGeoJsonSource(map, CHECKPOINT_SOURCE, toCheckpointFeatureCollection([]));
      ensureMapLayer(
        map,
        {
          id: CHECKPOINT_LAYER,
          type: "circle",
          source: CHECKPOINT_SOURCE,
          paint: CHECKPOINT_PIN_PAINT,
        },
        overlayBeforeId,
      );

      const currentMode = modeRef.current;
      const activeDraftRing = currentMode === "draw-boundary" ? draftRingRef.current : [];
      const savedBoundaries =
        boundaryForSiteRef.current !== null && currentMode !== "draw-boundary"
          ? [boundaryForSiteRef.current]
          : ([] as SiteBoundary[]);

      syncGeoJsonSource(map, BOUNDARY_SOURCE, toSiteBoundaryFeatureCollection(savedBoundaries));
      syncGeoJsonSource(map, DRAFT_FILL_SOURCE, toDraftBoundaryFillFeatureCollection(activeDraftRing));
      syncGeoJsonSource(map, DRAFT_LINE_SOURCE, toDraftBoundaryLineFeatureCollection(activeDraftRing));
      syncGeoJsonSource(map, DRAFT_VERTEX_SOURCE, toDraftBoundaryVertexFeatureCollection(activeDraftRing));
      syncGeoJsonSource(
        map,
        GEOFENCE_SOURCE,
        toGeofenceFeatureCollection(displayedCheckpointsRef.current),
      );
      syncGeoJsonSource(map, CHECKPOINT_SOURCE, toCheckpointFeatureCollection(displayedCheckpointsRef.current));
      syncGeoJsonSource(map, FIELD_PIN_SOURCE, toFieldPinFeatureCollection(fieldPinsRef.current));
      syncGeoJsonSource(
        map,
        SITE_CENTER_SOURCE,
        toSiteCenterFeatureCollection(siteCenterFor(activeSiteRef.current)),
      );
      repositionMapOverlayLayers(map, OVERLAY_LAYERS);
      syncCheckpointMarkers(map, checkpointMarkersRef.current, displayedCheckpointsRef.current);
      syncDraftVertexMarkers(
        map,
        draftMarkersRef.current,
        activeDraftRing,
        currentMode === "draw-boundary",
      );
      updateSvgOverlay();
      map.getCanvas().style.cursor =
        currentMode === "draw-boundary" || currentMode === "place-checkpoint" ? "crosshair" : "";
    };

    runMapOverlayRefresh(map, refresh);
  }, [updateSvgOverlay]);

  useEffect(() => {
    const map = mapRef.current;
    if (map === null || mapReadyVersion === 0) {
      return;
    }

    syncMapData();
    const stopWatching = watchCadOverlayStack(map, OVERLAY_LAYERS, syncMapData);

    return () => {
      stopWatching();
    };
  }, [mapReadyVersion, syncMapData]);

  useEffect(() => {
    if (mapReadyVersion === 0) {
      return;
    }
    syncMapData();
  }, [boundaryForSite, displayedCheckpoints, siteAssetsForSite, mode, draftRing, activeSite, mapReadyVersion, syncMapData]);

  useEffect(() => {
    const map = mapRef.current;
    if (map === null || mapReadyVersion === 0) {
      return;
    }
    whenMapStyleReady(map, () => {
      void frameMapViewport();
    });
  }, [boundaryForSite, displayedCheckpoints, activeSite, mapReadyVersion, frameMapViewport]);

  useEffect(() => {
    const map = mapRef.current;
    if (map === null || mapReadyVersion === 0 || recenterToken === 0) {
      return;
    }
    whenMapStyleReady(map, () => {
      map.resize();
      void frameMapViewport();
    });
  }, [recenterToken, mapReadyVersion, frameMapViewport]);

  useEffect(() => {
    setRecenterToken((value) => value + 1);
    setPendingCoords({});
    setSelectedCheckpointId(null);
    setMode("view");
    setDraftRing([]);
  }, [activeSiteId]);

  if (pickerSites.length === 0) {
    return (
      <p className="surface__state" role="status">
        Add a site in Setup first; the property map attaches to a site.
      </p>
    );
  }

  const selectedRow =
    selectedCheckpointId !== null
      ? uniqueSiteCheckpoints.find((row) => row.checkpointId === selectedCheckpointId) ?? null
      : null;
  const selectedPending =
    selectedRow !== null ? pendingCoords[selectedRow.checkpointId] : undefined;

  return (
    <div className="property-map">
      <div className="property-map__header">
        <label className="tpl__field property-map__site">
          Site
          <select
            aria-label="Site for property map"
            value={activeSiteId}
            onChange={(event) => {
              setSelectedSiteId(event.target.value);
              resetFeedback();
            }}
          >
            {pickerSites.map((site) => (
              <option key={site.siteId} value={site.siteId}>
                {site.siteName}
              </option>
            ))}
          </select>
        </label>
        <div className="property-map__actions">
          <button
            type="button"
            className="builder__navbtn"
            onClick={() => setRecenterToken((value) => value + 1)}
          >
            Recenter
          </button>
          {canManage && mode === "draw-boundary" ? (
            <p className="property-map__draw-status" role="status" aria-live="polite">
              {draftRing.length === 0
                ? "Click the map to place the first corner."
                : `${draftRing.length} corner${draftRing.length === 1 ? "" : "s"} placed${draftRing.length < 3 ? " (need at least 3)" : ""}.`}
            </p>
          ) : null}
          {canManage ? (
            mode === "draw-boundary" ? (
              <>
                <button type="button" className="builder__navbtn" disabled={busy} onClick={cancelDrawBoundary}>
                  Cancel draw
                </button>
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={busy || draftRing.length < 3}
                  onClick={() => void saveBoundary()}
                >
                  Save boundary
                </button>
              </>
            ) : (
              <button type="button" className="builder__navbtn" disabled={busy} onClick={enterDrawBoundary}>
                {boundaryForSite === null ? "Draw boundary" : "Redraw boundary"}
              </button>
            )
          ) : null}
          {canManage && boundaryForSite !== null && mode !== "draw-boundary" ? (
            <button type="button" className="builder__navbtn" disabled={busy} onClick={() => void clearBoundary()}>
              Clear boundary
            </button>
          ) : null}
          {canManage && mode === "place-checkpoint" && selectedRow !== null ? (
            <>
              <p className="property-map__draw-status" role="status" aria-live="polite">
                {selectedPending !== undefined
                  ? `${selectedRow.checkpointName} placed. Save to persist.`
                  : `Click the map to place ${selectedRow.checkpointName}.`}
              </p>
              <button
                type="button"
                className="builder__navbtn builder__navbtn--primary"
                disabled={busy || selectedPending === undefined}
                onClick={() => void saveCheckpointPlacement()}
              >
                Save stop location
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={() => {
                  setSelectedCheckpointId(null);
                  setMode("view");
                }}
              >
                Cancel
              </button>
            </>
          ) : null}
        </div>
      </div>

      {failure !== null ? (
        <p className="property-map__alert property-map__alert--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="property-map__alert property-map__alert--success" role="status">
          {receipt}
        </p>
      ) : null}

      <div className="property-map__body">
        <aside className="property-map__sidebar" aria-label="Stops on this site">
          <h2 className="property-map__sidebar-title">Stops on this site</h2>
          <ul className="property-map__stop-list">
            {uniqueSiteCheckpoints.map((row) => {
              const onMap = row.expectedLat !== null && row.expectedLng !== null;
              const pending = pendingCoords[row.checkpointId];
              const selected = selectedCheckpointId === row.checkpointId;
              const assetLabel = assetLabelForCheckpoint(row, pending);
              return (
                <li key={row.checkpointId}>
                  <button
                    type="button"
                    className={`property-map__stop${selected ? " property-map__stop--selected" : ""}`}
                    disabled={!canManage || busy}
                    onClick={() => {
                      resetFeedback();
                      if (!canManage) {
                        return;
                      }
                      setSelectedCheckpointId(row.checkpointId);
                      setMode("place-checkpoint");
                    }}
                  >
                    <span
                      className="property-map__stop-dot"
                      style={{ background: checkpointColor(row.verificationStatus) }}
                      aria-hidden
                    />
                    <span className="property-map__stop-label">
                      {row.checkpointName}
                      <span className="property-map__stop-meta">
                        {pending !== undefined
                          ? "Pending save"
                          : onMap
                            ? "On map"
                            : "Not on map"}
                        {assetLabel !== null ? ` · ${assetLabel}` : ""}
                        {row.routeName !== null ? ` · ${row.routeName}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {mode === "draw-boundary" ? (
            <p className="property-map__hint">
              Click the dirt pad to outline the property. Need at least 3 corners ({draftRing.length} so far).
            </p>
          ) : null}
        </aside>

        <div className="property-map__map-shell">
          {canManage && mode === "place-checkpoint" && selectedRow !== null ? (
            <div className="property-map__placement-bar" role="region" aria-label="Stop placement">
              <p className="property-map__placement-bar-copy">
                {selectedPending !== undefined ? (
                  <>
                    <strong>{selectedRow.checkpointName}</strong> ready at{" "}
                    {selectedPending.lat.toFixed(5)}, {selectedPending.lng.toFixed(5)}.
                  </>
                ) : (
                  <>
                    Click the map to place <strong>{selectedRow.checkpointName}</strong>.
                  </>
                )}
              </p>
              <div className="property-map__placement-bar-actions">
                <button
                  type="button"
                  className="builder__navbtn builder__navbtn--primary"
                  disabled={busy || selectedPending === undefined}
                  onClick={() => void saveCheckpointPlacement()}
                >
                  Save stop location
                </button>
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={busy}
                  onClick={() => {
                    setSelectedCheckpointId(null);
                    setMode("view");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
          {/* PRD-001Y Wave C legend. This surface has no layer-toggle rail, so the taxonomy is
              named in words next to its colour rather than left to be decoded from the map. */}
          <ul className="property-map__legend" aria-label="Pin types">
            {FIELD_PIN_LAYERS.map((layer) => {
              const style = fieldPinLayerStyle(layer);
              return (
                <li key={layer} className="property-map__legend-item">
                  <span
                    className="property-map__legend-dot"
                    style={{ background: style.color, borderColor: style.strokeColor }}
                    aria-hidden="true"
                  />
                  <span>{fieldPinLayerLabelWithCount(layer, fieldPinCounts[layer])}</span>
                </li>
              );
            })}
          </ul>
          <div ref={containerRef} className="property-map__canvas" aria-label="Property map" />
          <svg ref={svgOverlayRef} className="property-map__svg-overlay" aria-hidden="true" />
          {boundaryModel.status === "loading" || checkpointModel.status === "loading" ? (
            <p className="property-map__overlay" role="status">
              Loading map…
            </p>
          ) : null}
        </div>
      </div>

      <p className="page__note property-map__footer">
        <Link to="/setup">Back to Setup</Link>
        {" · "}
        Saved boundaries and stops appear on Command Center when layer toggles are on.
        {siteAssetsForSite.length > 0 ? " Indigo outlines are L1 asset zones from Setup." : ""}
        {geofencePreviewCount === 0 && displayedCheckpoints.length > 0
          ? " Geofence rings preview only when a stop has a GPS radius set in Setup."
          : null}
        {stackedStopCount > 0
          ? ` ${stackedStopCount} stop${stackedStopCount === 1 ? "" : "s"} share the same coordinates and are fanned out on the map.`
          : null}
      </p>
    </div>
  );
}
